import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react';
import { getSlideSets, SlideSet } from '../../../../api/slide-set';
import useThrowAsyncError from '../../../../hooks/useThrowAsyncError';
import ModuleContext from '../../_contexts/ModuleContext';
import TableOfContentsContext from './TableOfContentsContext';

type SlideSetsContextValue = {
  slideSets: SlideSet[];
  setSlideSets: React.Dispatch<React.SetStateAction<SlideSet[]>>;
  fetchSlideSets: (updateLoadingState?: boolean) => void;
  isLoading: boolean;
};

const SlideSetsContext = createContext<SlideSetsContextValue | undefined>(undefined);

export default SlideSetsContext;

export function SlideSetsContextProvider({ children }: PropsWithChildren) {
  const handleError = useThrowAsyncError();

  const { selectedModule } = useContext(ModuleContext);
  const { selectedNode } = useContext(TableOfContentsContext);

  const [slideSets, setSlideSets] = useState<SlideSet[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSlideSets = useCallback(
    async (updateLoadingState = true) => {
      if (!selectedModule || !selectedNode) return;

      if (!selectedModule.enableSlideSetsAccess) {
        setIsLoading(false);

        return;
      }

      if (updateLoadingState) setIsLoading(true);

      try {
        const data = await getSlideSets(selectedModule.id, selectedNode.id);

        setSlideSets(data);
      } catch (e) {
        handleError(e);
      } finally {
        if (updateLoadingState) setIsLoading(false);
      }
    },
    [handleError, selectedModule, selectedNode],
  );

  useEffect(() => {
    fetchSlideSets();
  }, [fetchSlideSets]);

  return (
    <SlideSetsContext.Provider value={{ slideSets, setSlideSets, fetchSlideSets, isLoading }}>
      {children}
    </SlideSetsContext.Provider>
  );
}
